import { PointCloudWireframe } from "@/components/devtools/point-cloud-wireframe";
import {
  selectEntityIdToColorMap,
  selectIsScanHighlighted,
} from "@/data-preparation-tool/store/data-preparation-ui/data-preparation-ui-selectors";
import { PointCloudObject } from "@/object-cache";
import { Features, selectHasFeature } from "@/store/features/features-slice";
import { useAppSelector } from "@/store/store-hooks";
import { useIsDarkScheme } from "@faro-lotv/flat-ui";
import { GUID } from "@faro-lotv/foundation";
import type { JSX } from "react";
import { Matrix4 } from "three";
import { useLodVisualizationSettings } from "./lod-visualization-settings";

type ScanEntityPointCloudWireframeProps = {
  /** The ID of the scan to show the wireframe of. */
  scanEntityId: GUID;
  /** The point cloud to show the wireframe for. */
  pointCloudObject: PointCloudObject;
  /** The position to apply to the wireframe. */
  transform: Matrix4;
  /** Whether the scan is currently being shown. */
  isVisible: boolean;
};

/**
 * @returns Debug wireframe view of the LOD structure of a scan.
 *  Only visible when the rendering debug feature is enabled.
 */
export function ScanEntityPointCloudWireframe({
  scanEntityId,
  pointCloudObject,
  transform,
  isVisible,
}: ScanEntityPointCloudWireframeProps): JSX.Element | null {
  const hasRenderingDebugFeature = useAppSelector(
    selectHasFeature(Features.RenderingDebug),
  );
  const { isEnabled, nodeType } = useLodVisualizationSettings();
  const isDarkMode = useIsDarkScheme();
  const colorMap = useAppSelector(selectEntityIdToColorMap(isDarkMode));
  const isHighlighted = useAppSelector(selectIsScanHighlighted(scanEntityId));

  if (!hasRenderingDebugFeature || !isEnabled) return null;

  return (
    <PointCloudWireframe
      pointCloudObject={pointCloudObject}
      transform={transform}
      color={colorMap[scanEntityId]}
      nodeType={nodeType}
      isVisible={isVisible && isHighlighted}
    />
  );
}
